import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { backendURL } from "../config";
import {
    Shield, RefreshCw, AlertTriangle, CheckCircle2, ChevronDown, ChevronRight,
    Globe, Clock, Eye, Loader2, Zap, Database, FileText, ExternalLink,
    ShieldAlert, ShieldCheck, ShieldX, Search, Server, Target, Plus, X,
    Filter, Sparkles, Terminal, Activity, Layers, Lock, UserCheck, ArrowRight,
    Check, ChevronLeft
} from "lucide-react";

const verdictStyles = {
    "Malicious":  { bg: "bg-rose-500/15", border: "border-rose-500/30", text: "text-rose-400", dot: "bg-rose-500", label: "Malicious" },
    "Suspicious": { bg: "bg-amber-500/15", border: "border-amber-500/30", text: "text-amber-400", dot: "bg-amber-500", label: "Suspicious" },
    "No Hits":    { bg: "bg-emerald-500/15", border: "border-emerald-500/30", text: "text-emerald-400", dot: "bg-emerald-400", label: "No hits detected" },
    "Benign":     { bg: "bg-slate-500/15", border: "border-slate-500/30", text: "text-slate-400", dot: "bg-slate-400", label: "Benign" },
};

const sourceStyles = {
    "Google Threat Intelligence": "text-indigo-400",
    "Mandiant Intelligence": "text-blue-400",
    "CISA / NSA Advisory": "text-orange-400",
    "CISA KEV": "text-blue-400",
    "CrowdStrike Intelligence": "text-rose-400",
    "Abuse.ch Feodo": "text-amber-400",
    "Abuse.ch URLhaus": "text-purple-400",
};

export default function ThreatIntel() {
    const navigate = useNavigate();
    const [reports, setReports] = useState([]);
    const [stats, setStats] = useState({});
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [rehunting, setRehunting] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [relevantOnly, setRelevantOnly] = useState(false);
    const [filterVerdict, setFilterVerdict] = useState("all");
    const [selectedReport, setSelectedReport] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 20;

    const [isIngestModalOpen, setIsIngestModalOpen] = useState(false);
    const [ingestForm, setIngestForm] = useState({
        title: "",
        source: "Google Threat Intelligence",
        actor: "",
        targetIndustry: "",
        targetRegion: "US, CA",
        reportText: ""
    });
    const [ingesting, setIngesting] = useState(false);

    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        fetchReports();
    }, []);

    const fetchReports = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${backendURL}/api/threat-intel/reports`, { headers });
            setReports(res.data.reports || []);
            setStats(res.data.stats || {});
        } catch (err) {
            if (err.response?.status === 401) {
                localStorage.removeItem("token");
                window.location.href = "/login";
                return;
            }
            console.error("Failed to fetch reports:", err);
        }
        setLoading(false);
    };

    const syncFeeds = async () => {
        setSyncing(true);
        try {
            await axios.post(`${backendURL}/api/threat-intel/sync`, {}, { headers, timeout: 120000 });
            await fetchReports();
        } catch (err) {
            console.error("Feed sync error:", err);
        }
        setSyncing(false);
    };

    const rehunt = async (reportId, e) => {
        if (e) e.stopPropagation();
        setRehunting(reportId);
        try {
            const res = await axios.post(`${backendURL}/api/threat-intel/hunt/${reportId}`, {}, { headers, timeout: 60000 });
            await fetchReports();
            if (selectedReport && selectedReport.reportId === reportId) {
                setSelectedReport(prev => ({
                    ...prev,
                    verdict: res.data.verdict,
                    hitsInEnv: res.data.huntResult?.totalHits || 0,
                    huntResult: res.data.huntResult,
                    lastScanned: res.data.lastScanned
                }));
            }
        } catch (err) {
            console.error("Rehunt failed:", err);
        }
        setRehunting(null);
    };

    const handleIngestSubmit = async (e) => {
        e.preventDefault();
        if (!ingestForm.title || !ingestForm.reportText) return;

        setIngesting(true);
        try {
            const res = await axios.post(`${backendURL}/api/threat-intel/ingest`, ingestForm, { headers });
            setIsIngestModalOpen(false);
            setIngestForm({ title: "", source: "Google Threat Intelligence", actor: "", targetIndustry: "", targetRegion: "US, CA", reportText: "" });
            await fetchReports();
            if (res.data.report) setSelectedReport(res.data.report);
        } catch (err) {
            console.error("Ingestion failed:", err);
        }
        setIngesting(false);
    };

    const applyTemplate = (template) => {
        setIngestForm(template);
    };

    const formatDate = (isoStr) => {
        if (!isoStr) return "—";
        const d = new Date(isoStr);
        return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
    };

    const handleSearchChange = (val) => {
        setSearchQuery(val);
        setCurrentPage(1);
    };

    const handleRelevantToggle = () => {
        setRelevantOnly(!relevantOnly);
        setCurrentPage(1);
    };

    const handleVerdictFilter = (v) => {
        setFilterVerdict(v);
        setCurrentPage(1);
    };

    // Filter Logic
    const filteredReports = reports.filter(r => {
        if (relevantOnly && (!r.hitsInEnv || r.hitsInEnv === 0) && r.verdict !== "Malicious" && r.verdict !== "Suspicious") {
            return false;
        }
        if (filterVerdict !== "all" && r.verdict?.toLowerCase() !== filterVerdict.toLowerCase()) {
            return false;
        }
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            return (
                r.title?.toLowerCase().includes(q) ||
                r.actor?.toLowerCase().includes(q) ||
                r.targetIndustry?.toLowerCase().includes(q) ||
                r.targetRegion?.toLowerCase().includes(q) ||
                r.source?.toLowerCase().includes(q) ||
                (r.iocs?.ips && r.iocs.ips.some(ip => ip.includes(q))) ||
                (r.iocs?.cves && r.iocs.cves.some(c => c.toLowerCase().includes(q))) ||
                (r.iocs?.domains && r.iocs.domains.some(d => d.toLowerCase().includes(q)))
            );
        }
        return true;
    });

    const totalPages = Math.max(1, Math.ceil(filteredReports.length / pageSize));
    const paginatedReports = filteredReports.slice((currentPage - 1) * pageSize, currentPage * pageSize);
    const startItem = filteredReports.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;
    const endItem = Math.min(currentPage * pageSize, filteredReports.length);

    const threatHuntsCount = stats.threatHuntsCount ?? reports.length;
    const iocsFoundCount = stats.iocsFoundCount ?? 0;

    return (
        <div className="w-full space-y-6 pb-12">
            {/* ─── Breadcrumb Header ─── */}
            <div className="flex items-center gap-2 text-[11px] font-mono text-slate-500 uppercase tracking-wider">
                <span>02</span>
                <span>/</span>
                <span className="text-slate-400">Hunt on Threat Intel</span>
            </div>

            {/* ─── Page Title & Action Controls ─── */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div>
                    <h1 className="text-[24px] lg:text-[28px] font-extrabold text-white tracking-tight">
                        Every threat report, hunted automatically.
                    </h1>
                    <p className="text-[13px] text-slate-400 mt-1 max-w-3xl leading-relaxed">
                        Connect trusted threat intelligence feeds to automatically hunt <strong className="text-white font-semibold">actors, techniques, and indicators</strong> against your 636k+ real-time telemetry logs. Each report is actively correlated as malicious, suspicious, or no hits.
                    </p>
                </div>

                <div className="flex items-center gap-3 self-start lg:self-auto">
                    <button
                        onClick={syncFeeds}
                        disabled={syncing}
                        className="flex items-center gap-2 px-3.5 py-2 bg-[#111827] border border-white/[0.08] hover:border-white/[0.15] text-slate-300 rounded-lg text-[12px] font-semibold transition-all hover:text-white disabled:opacity-50"
                    >
                        <RefreshCw className={`w-3.5 h-3.5 ${syncing ? "animate-spin text-orange-400" : ""}`} />
                        Sync Feeds
                    </button>

                    <button
                        onClick={() => setIsIngestModalOpen(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-600 via-rose-600 to-indigo-600 hover:from-orange-500 hover:to-indigo-500 text-white rounded-lg text-[12px] font-bold shadow-lg shadow-orange-600/20 transition-all hover:scale-[1.02]"
                    >
                        <Plus className="w-4 h-4" />
                        Ingest TI Report
                    </button>
                </div>
            </div>

            {/* ─── QueryTel Clean Minimalist KPI Cards ─── */}
            <div className="flex flex-wrap items-center gap-4">
                {/* 1. Threat Intel Hunts */}
                <div className="w-44 sm:w-52 bg-[#111827] border border-white/[0.08] rounded-xl p-4 space-y-1 shadow-sm">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Threat Intel Hunts</p>
                    <div className="text-[32px] font-black text-white tracking-tight leading-none pt-1">
                        {threatHuntsCount}
                    </div>
                </div>

                {/* 2. IOCs Found */}
                <div className="w-44 sm:w-52 bg-[#111827] border border-white/[0.08] rounded-xl p-4 space-y-1 shadow-sm">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">IOCs Found</p>
                    <div className="text-[32px] font-black text-white tracking-tight leading-none pt-1">
                        {iocsFoundCount}
                    </div>
                </div>
            </div>

            {/* ─── Filter & Search Toolbar ─── */}
            <div className="bg-[#111827] border border-white/[0.06] rounded-xl p-4 flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2.5 flex-1">
                    {/* Search Input */}
                    <div className="flex items-center bg-black/30 border border-white/[0.08] rounded-lg px-3 py-1.5 w-full md:w-80">
                        <Search className="w-3.5 h-3.5 text-slate-500 mr-2" />
                        <input
                            type="text"
                            placeholder="Search report, actor, industry, IOC..."
                            value={searchQuery}
                            onChange={(e) => handleSearchChange(e.target.value)}
                            className="bg-transparent border-none outline-none text-[12px] text-slate-200 placeholder:text-slate-600 w-full"
                        />
                    </div>

                    {/* Filter Chip: Relevant Reports Only */}
                    <button
                        onClick={handleRelevantToggle}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold border transition-all ${
                            relevantOnly
                                ? "bg-rose-500/20 border-rose-500/40 text-rose-300 shadow-sm"
                                : "bg-black/20 border-white/[0.08] text-slate-400 hover:text-slate-200"
                        }`}
                    >
                        <span>Relevant reports only</span>
                        {relevantOnly && <X className="w-3 h-3 text-rose-400" />}
                    </button>

                    {/* Quick Verdict Filters */}
                    <div className="flex items-center bg-black/30 border border-white/[0.08] rounded-lg p-0.5 text-[11px]">
                        {["all", "Malicious", "Suspicious", "No Hits"].map(v => (
                            <button
                                key={v}
                                onClick={() => handleVerdictFilter(v)}
                                className={`px-2.5 py-1 rounded-md capitalize transition-all ${
                                    filterVerdict === v
                                        ? "bg-indigo-600 text-white font-semibold"
                                        : "text-slate-400 hover:text-slate-200"
                                }`}
                            >
                                {v === "No Hits" ? "No Hits Detected" : v}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="text-[11px] text-slate-500 font-mono self-end md:self-auto">
                    Showing <strong className="text-slate-300">{startItem}–{endItem}</strong> of {filteredReports.length} reports
                </div>
            </div>

            {/* ─── QueryTel Main Threat Intel Reports Table ─── */}
            <div className="bg-[#111827] border border-white/[0.06] rounded-xl overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-[12px] border-collapse">
                        <thead>
                            <tr className="bg-black/30 border-b border-white/[0.06] text-slate-400 font-bold uppercase text-[10px] tracking-wider">
                                <th className="py-3.5 px-4 font-semibold">Report</th>
                                <th className="py-3.5 px-4 font-semibold">Actor</th>
                                <th className="py-3.5 px-4 font-semibold">Targeted Industry</th>
                                <th className="py-3.5 px-4 font-semibold">Targeted Region</th>
                                <th className="py-3.5 px-4 font-semibold">Published</th>
                                <th className="py-3.5 px-4 font-semibold text-right">Investigation</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/[0.03]">
                            {loading ? (
                                <tr>
                                    <td colSpan={6} className="py-12 text-center text-slate-500">
                                        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-indigo-400" />
                                        Loading autonomous threat intel reports...
                                    </td>
                                </tr>
                            ) : paginatedReports.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="py-12 text-center text-slate-500">
                                        <ShieldCheck className="w-8 h-8 mx-auto mb-2 text-slate-600 opacity-50" />
                                        No threat intel reports match your search criteria.
                                    </td>
                                </tr>
                            ) : (
                                paginatedReports.map((report) => {
                                    const vStyle = verdictStyles[report.verdict] || verdictStyles["No Hits"];
                                    const srcColor = sourceStyles[report.source] || "text-slate-400";
                                    const isSelected = selectedReport?.reportId === report.reportId;

                                    return (
                                        <tr
                                            key={report.reportId}
                                            onClick={() => navigate(`/threat-intel/${report.reportId}`)}
                                            className="cursor-pointer transition-all hover:bg-white/[0.03] group"
                                        >
                                            {/* Report Title & Feed Source */}
                                            <td className="py-3.5 px-4 max-w-sm">
                                                <div className="space-y-0.5">
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-bold text-slate-100 hover:text-indigo-400 transition-colors leading-snug line-clamp-1">
                                                            {report.title}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-1.5 text-[10px]">
                                                        <span className={`font-semibold ${srcColor}`}>{report.source}</span>
                                                        {report.iocsCount > 0 && (
                                                            <>
                                                                <span className="text-slate-600">•</span>
                                                                <span className="text-slate-500 font-mono">{report.iocsCount} IOCs</span>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Threat Actor */}
                                            <td className="py-3.5 px-4">
                                                {report.actor ? (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-[10.5px] font-bold font-mono bg-white/[0.04] text-slate-300 border border-white/[0.08]">
                                                        {report.actor}
                                                    </span>
                                                ) : (
                                                    <span className="text-slate-600">—</span>
                                                )}
                                            </td>

                                            {/* Targeted Industry */}
                                            <td className="py-3.5 px-4 text-slate-300 font-medium">
                                                {report.targetIndustry || "—"}
                                            </td>

                                            {/* Targeted Region */}
                                            <td className="py-3.5 px-4">
                                                {report.targetRegion ? (
                                                    <span className="font-mono text-slate-400 text-[11px]">
                                                        {report.targetRegion}
                                                    </span>
                                                ) : (
                                                    <span className="text-slate-600">—</span>
                                                )}
                                            </td>

                                            {/* Published Date */}
                                            <td className="py-3.5 px-4 text-slate-400 text-[11px] whitespace-nowrap">
                                                {formatDate(report.publishedDate)}
                                            </td>

                                            {/* Investigation Verdict Pill */}
                                            <td className="py-3.5 px-4 text-right whitespace-nowrap">
                                                <div className="inline-flex items-center gap-2">
                                                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold border ${vStyle.bg} ${vStyle.border} ${vStyle.text}`}>
                                                        <span className={`w-1.5 h-1.5 rounded-full ${vStyle.dot}`} />
                                                        {report.verdict === "No Hits" ? "No hits detected" : report.verdict}
                                                    </span>

                                                    <button
                                                        onClick={(e) => rehunt(report.reportId, e)}
                                                        disabled={rehunting === report.reportId}
                                                        title="Re-hunt this report across all logs"
                                                        className="p-1 hover:bg-white/[0.06] rounded text-slate-500 hover:text-white transition-colors"
                                                    >
                                                        <RefreshCw className={`w-3.5 h-3.5 ${rehunting === report.reportId ? "animate-spin text-indigo-400" : ""}`} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {/* ─── 20-Item Pager Toolbar ─── */}
                {totalPages > 1 && (
                    <div className="border-t border-white/[0.06] p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-black/20 text-[12px] text-slate-400">
                        <div>
                            Showing <strong className="text-white font-mono">{startItem}</strong> to{" "}
                            <strong className="text-white font-mono">{endItem}</strong> of{" "}
                            <strong className="text-white font-mono">{filteredReports.length}</strong> reports
                        </div>

                        <div className="flex items-center gap-1.5">
                            {/* Prev Button */}
                            <button
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                                className="flex items-center gap-1 px-3 py-1.5 bg-white/[0.04] hover:bg-white/[0.08] disabled:opacity-40 disabled:hover:bg-white/[0.04] text-slate-300 rounded-md font-medium transition-all"
                            >
                                <ChevronLeft className="w-4 h-4" />
                                Prev
                            </button>

                            {/* Page Number Pills */}
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                                <button
                                    key={pageNum}
                                    onClick={() => setCurrentPage(pageNum)}
                                    className={`w-8 h-8 rounded-md font-mono text-[11px] font-bold transition-all ${
                                        currentPage === pageNum
                                            ? "bg-indigo-600 text-white shadow-sm"
                                            : "bg-white/[0.04] hover:bg-white/[0.08] text-slate-300"
                                    }`}
                                >
                                    {pageNum}
                                </button>
                            ))}

                            {/* Next Button */}
                            <button
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                disabled={currentPage === totalPages}
                                className="flex items-center gap-1 px-3 py-1.5 bg-white/[0.04] hover:bg-white/[0.08] disabled:opacity-40 disabled:hover:bg-white/[0.04] text-slate-300 rounded-md font-medium transition-all"
                            >
                                Next
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* ─── Report Forensic Detail Drawer ─── */}
            {selectedReport && (
                <div className="fixed inset-y-0 right-0 z-50 w-full max-w-xl bg-[#0F172A] border-l border-white/[0.08] shadow-2xl shadow-black flex flex-col justify-between animate-in slide-in-from-right duration-200">
                    <div className="p-6 overflow-y-auto space-y-6">
                        {/* Header */}
                        <div className="flex items-start justify-between gap-4 border-b border-white/[0.06] pb-4">
                            <div>
                                <div className="flex items-center gap-2 mb-1.5">
                                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase border ${verdictStyles[selectedReport.verdict]?.bg} ${verdictStyles[selectedReport.verdict]?.border} ${verdictStyles[selectedReport.verdict]?.text}`}>
                                        {selectedReport.verdict === "No Hits" ? "No hits detected" : selectedReport.verdict}
                                    </span>
                                    <span className="text-[10px] text-slate-500 font-mono">{selectedReport.reportId}</span>
                                </div>
                                <h3 className="text-[16px] font-bold text-white leading-snug">{selectedReport.title}</h3>
                                <p className="text-[11px] text-slate-400 mt-1 flex items-center gap-2">
                                    <span>Source: <strong className="text-indigo-400">{selectedReport.source}</strong></span>
                                    <span>•</span>
                                    <span>{formatDate(selectedReport.publishedDate)}</span>
                                </p>
                            </div>

                            <button
                                onClick={() => setSelectedReport(null)}
                                className="p-1.5 bg-white/[0.04] hover:bg-white/[0.08] rounded-lg text-slate-400 hover:text-white transition-colors"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        {/* Executive Summary */}
                        <div className="bg-black/30 border border-white/[0.04] rounded-xl p-4 space-y-2">
                            <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                                <FileText className="w-3.5 h-3.5 text-indigo-400" />
                                Threat Intelligence Brief
                            </h4>
                            <p className="text-[12px] text-slate-300 leading-relaxed">{selectedReport.description}</p>
                        </div>

                        {/* Target & Actor Metadata Cards */}
                        <div className="grid grid-cols-3 gap-2.5 text-[11px]">
                            <div className="p-3 bg-black/20 rounded-lg border border-white/[0.04]">
                                <p className="text-slate-500 text-[10px] uppercase font-semibold">Threat Actor</p>
                                <p className="text-slate-200 font-bold font-mono mt-1">{selectedReport.actor || "Unidentified"}</p>
                            </div>
                            <div className="p-3 bg-black/20 rounded-lg border border-white/[0.04]">
                                <p className="text-slate-500 text-[10px] uppercase font-semibold">Target Industry</p>
                                <p className="text-slate-200 font-medium mt-1 truncate">{selectedReport.targetIndustry || "Cross-Industry"}</p>
                            </div>
                            <div className="p-3 bg-black/20 rounded-lg border border-white/[0.04]">
                                <p className="text-slate-500 text-[10px] uppercase font-semibold">Target Region</p>
                                <p className="text-slate-200 font-mono mt-1">{selectedReport.targetRegion || "Global"}</p>
                            </div>
                        </div>

                        {/* MITRE ATT&CK Techniques */}
                        {selectedReport.mitreTechniques && selectedReport.mitreTechniques.length > 0 && (
                            <div className="space-y-2">
                                <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                                    <Layers className="w-3.5 h-3.5 text-purple-400" />
                                    MITRE ATT&CK Techniques
                                </h4>
                                <div className="flex flex-wrap gap-1.5">
                                    {selectedReport.mitreTechniques.map((tech, idx) => (
                                        <span key={idx} className="px-2.5 py-1 bg-purple-500/10 border border-purple-500/20 text-purple-300 rounded-md text-[11px] font-mono">
                                            {tech}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Matched Log Evidence in Environment */}
                        <div className="space-y-2.5">
                            <div className="flex items-center justify-between">
                                <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                                    <Activity className="w-3.5 h-3.5 text-rose-400" />
                                    Internal Telemetry Matches ({selectedReport.hitsInEnv || 0})
                                </h4>
                                {selectedReport.hitsInEnv > 0 && (
                                    <span className="text-[10px] font-bold text-rose-400 bg-rose-500/10 px-2 py-0.5 rounded border border-rose-500/20">
                                        Active Threat Evidence
                                    </span>
                                )}
                            </div>

                            {selectedReport.huntResult?.evidence && selectedReport.huntResult.evidence.length > 0 ? (
                                <div className="space-y-2 max-h-56 overflow-y-auto custom-scrollbar">
                                    {selectedReport.huntResult.evidence.map((ev, idx) => (
                                        <div key={idx} className="p-3 bg-black/40 border border-rose-500/20 rounded-lg text-[11px] space-y-1">
                                            <div className="flex items-center justify-between text-slate-400 font-mono text-[10px]">
                                                <span>{formatDate(ev.ts)}</span>
                                                <span className="font-semibold text-rose-400">{ev.category || "Security Alert"}</span>
                                            </div>
                                            <div className="flex items-center gap-2 font-mono text-slate-200 text-[11px]">
                                                <span className="text-blue-400">{ev.sourceIp}</span>
                                                <ArrowRight className="w-3 h-3 text-slate-600" />
                                                <span className="text-purple-400">{ev.destIp}</span>
                                            </div>
                                            <p className="text-[11px] text-slate-300">{ev.message}</p>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="p-4 bg-black/20 border border-white/[0.04] rounded-lg text-center text-slate-500 text-[11px]">
                                    <ShieldCheck className="w-5 h-5 text-emerald-400 mx-auto mb-1 opacity-70" />
                                    No matched IOCs found across your active telemetry logs.
                                </div>
                            )}
                        </div>

                        {/* Extracted IOCs */}
                        {selectedReport.iocs && (
                            <div className="space-y-2">
                                <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                                    <Terminal className="w-3.5 h-3.5 text-indigo-400" />
                                    Extracted Indicators ({selectedReport.iocsCount || 0})
                                </h4>
                                <div className="bg-black/30 border border-white/[0.04] rounded-lg p-3 space-y-1.5 max-h-40 overflow-y-auto custom-scrollbar font-mono text-[11px]">
                                    {selectedReport.iocs.ips?.map((ip, idx) => (
                                        <div key={idx} className="flex items-center justify-between text-slate-300">
                                            <span className="text-blue-400">IP: {ip}</span>
                                            <span className="text-[10px] text-slate-500">C2 / Inbound</span>
                                        </div>
                                    ))}
                                    {selectedReport.iocs.domains?.map((dom, idx) => (
                                        <div key={idx} className="flex items-center justify-between text-slate-300">
                                            <span className="text-indigo-400">Domain: {dom}</span>
                                            <span className="text-[10px] text-slate-500">Phishing / Host</span>
                                        </div>
                                    ))}
                                    {selectedReport.iocs.cves?.map((cve, idx) => (
                                        <div key={idx} className="flex items-center justify-between text-slate-300">
                                            <span className="text-rose-400">CVE: {cve}</span>
                                            <span className="text-[10px] text-slate-500">Exploit</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Drawer Footer Actions */}
                    <div className="p-5 border-t border-white/[0.06] bg-black/40 flex items-center gap-3">
                        <button
                            onClick={() => rehunt(selectedReport.reportId)}
                            disabled={rehunting === selectedReport.reportId}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white text-[12px] font-bold rounded-lg transition-colors disabled:opacity-50"
                        >
                            <RefreshCw className={`w-3.5 h-3.5 ${rehunting === selectedReport.reportId ? "animate-spin" : ""}`} />
                            Re-Hunt Across Telemetry Logs
                        </button>

                        <button
                            onClick={() => setSelectedReport(null)}
                            className="px-4 py-2.5 bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 text-[12px] font-semibold rounded-lg transition-colors"
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}

            {/* ─── Ingest TI Report Modal ─── */}
            {isIngestModalOpen && (
                <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-[#0F172A] border border-white/[0.08] rounded-2xl w-full max-w-2xl overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200">
                        <div className="p-6 border-b border-white/[0.06] flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-orange-500 to-rose-600 flex items-center justify-center shadow-lg shadow-orange-500/20">
                                    <Plus className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                    <h3 className="text-[16px] font-bold text-white">Ingest Threat Intel Report</h3>
                                    <p className="text-[11px] text-slate-400">Paste any CTI advisory or IOC block — QueryTel extracts entities and auto-hunts immediately.</p>
                                </div>
                            </div>

                            <button
                                onClick={() => setIsIngestModalOpen(false)}
                                className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-white/[0.04]"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        {/* Quick Templates */}
                        <div className="px-6 pt-4 pb-2">
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Quick Start Templates</p>
                            <div className="flex flex-wrap gap-2">
                                {[
                                    {
                                        title: "Volt Typhoon PAN-OS Zero-Day Advisory",
                                        source: "CISA / NSA Advisory",
                                        actor: "Volt Typhoon",
                                        targetIndustry: "Critical Infrastructure",
                                        targetRegion: "US, GU",
                                        reportText: "State-sponsored actor Volt Typhoon targeting PAN-OS devices using CVE-2024-3400 command injection. C2 IPs: 104.244.76.13, 198.144.149.20. Domains: telemetry-edge-update.com"
                                    },
                                    {
                                        title: "Scattered Spider Okta & Identity Compromise",
                                        source: "Mandiant Intelligence",
                                        actor: "Scattered Spider (UNC3944)",
                                        targetIndustry: "Financial Services / Telecom",
                                        targetRegion: "US, UK",
                                        reportText: "UNC3944 SIM-swapping campaign targeting Okta SSO tokens and Helpdesk MFA resets. IOCs: 185.220.101.5, sso-verification-gateway.net"
                                    }
                                ].map((tmpl, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => applyTemplate(tmpl)}
                                        className="text-[11px] font-medium bg-white/[0.03] hover:bg-white/[0.08] border border-white/[0.08] text-slate-300 px-3 py-1 rounded-lg transition-all"
                                    >
                                        {tmpl.title}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <form onSubmit={handleIngestSubmit} className="p-6 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1">
                                    <label className="text-[11px] font-semibold text-slate-300">Report Title *</label>
                                    <input
                                        type="text"
                                        required
                                        placeholder="e.g., Volt Typhoon Reconnaissance Brief"
                                        value={ingestForm.title}
                                        onChange={(e) => setIngestForm({ ...ingestForm, title: e.target.value })}
                                        className="w-full bg-black/40 border border-white/[0.08] rounded-lg px-3 py-2 text-[12px] text-white placeholder:text-slate-600 outline-none focus:border-indigo-500"
                                    />
                                </div>

                                <div className="space-y-1">
                                    <label className="text-[11px] font-semibold text-slate-300">Feed / Source Name</label>
                                    <input
                                        type="text"
                                        placeholder="e.g., Google Threat Intelligence"
                                        value={ingestForm.source}
                                        onChange={(e) => setIngestForm({ ...ingestForm, source: e.target.value })}
                                        className="w-full bg-black/40 border border-white/[0.08] rounded-lg px-3 py-2 text-[12px] text-white placeholder:text-slate-600 outline-none focus:border-indigo-500"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-3 gap-3">
                                <div className="space-y-1">
                                    <label className="text-[11px] font-semibold text-slate-300">Threat Actor</label>
                                    <input
                                        type="text"
                                        placeholder="e.g., APT42, FIN7"
                                        value={ingestForm.actor}
                                        onChange={(e) => setIngestForm({ ...ingestForm, actor: e.target.value })}
                                        className="w-full bg-black/40 border border-white/[0.08] rounded-lg px-3 py-2 text-[12px] text-white placeholder:text-slate-600 outline-none focus:border-indigo-500"
                                    />
                                </div>

                                <div className="space-y-1">
                                    <label className="text-[11px] font-semibold text-slate-300">Targeted Industry</label>
                                    <input
                                        type="text"
                                        placeholder="e.g., Energy, Defense"
                                        value={ingestForm.targetIndustry}
                                        onChange={(e) => setIngestForm({ ...ingestForm, targetIndustry: e.target.value })}
                                        className="w-full bg-black/40 border border-white/[0.08] rounded-lg px-3 py-2 text-[12px] text-white placeholder:text-slate-600 outline-none focus:border-indigo-500"
                                    />
                                </div>

                                <div className="space-y-1">
                                    <label className="text-[11px] font-semibold text-slate-300">Targeted Region</label>
                                    <input
                                        type="text"
                                        placeholder="e.g., US, CA, UK"
                                        value={ingestForm.targetRegion}
                                        onChange={(e) => setIngestForm({ ...ingestForm, targetRegion: e.target.value })}
                                        className="w-full bg-black/40 border border-white/[0.08] rounded-lg px-3 py-2 text-[12px] text-white placeholder:text-slate-600 outline-none focus:border-indigo-500"
                                    />
                                </div>
                            </div>

                            <div className="space-y-1">
                                <label className="text-[11px] font-semibold text-slate-300">Advisory Text & IOCs *</label>
                                <textarea
                                    required
                                    rows={4}
                                    placeholder="Paste CTI advisory text, CVE codes, C2 IP addresses, domains, or file hashes..."
                                    value={ingestForm.reportText}
                                    onChange={(e) => setIngestForm({ ...ingestForm, reportText: e.target.value })}
                                    className="w-full bg-black/40 border border-white/[0.08] rounded-lg px-3 py-2 text-[12px] text-white placeholder:text-slate-600 outline-none focus:border-indigo-500 font-mono"
                                />
                            </div>

                            <div className="pt-2 flex items-center justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => setIsIngestModalOpen(false)}
                                    className="px-4 py-2 bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 rounded-lg text-[12px] font-semibold transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={ingesting}
                                    className="flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-orange-600 to-indigo-600 hover:from-orange-500 hover:to-indigo-500 text-white rounded-lg text-[12px] font-bold shadow-lg transition-all disabled:opacity-50"
                                >
                                    {ingesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                                    Auto-Hunt & Ingest
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
